import Axios from 'axios';
import React, { Fragment, useEffect, useState } from 'react';
import config from '../../Config/key';
import { DEFAULT_BCC_EMAIL } from '../../Config/mailConfig';
import {
  Table,
  Button,
  message,
  Typography,
  Select,
  Modal,
  Input,
  DatePicker,
  Popconfirm,
} from 'antd';
import { PlusOutlined, DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { PageContainer } from '../../common';

const { Text } = Typography;
const { confirm } = Modal;
const { Option } = Select;

const MultiMailSendReservatePage = (props) => {
  const [groups, setGroups] = useState([]); // グループ名とグループコード
  const [selGroupInfo, setSelGroupInfo] = useState(null); // 選択されたグループ情報
  const [selGroupCode, setSelGroupCode] = useState(''); // 選択されたグループコード
  const [survey, setSurvey] = useState([]); // サーベイ名とサーベイコード
  const [mtInfo, setMtInfo] = useState([]); // メールテンプレート情報
  const [reservations, setReservations] = useState([]); // 予約リスト
  const usr = useSelector((state) => state.user);

  // データベースから基本情報を読み込み
  const readDB = async () => {
    try {
      if (!usr.userData) return null;

      const [surveyInfo, groupInfo, mtInfo] = await Promise.all([
        Axios.get('/api/survey/'), // DBからサーベイ情報取得
        Axios.get('/api/group/'), // DBからグループ情報取得
        Axios.get('/api/mailTemplate/'),
      ]);
      setGroups(groupInfo.data.groups);
      setSurvey(surveyInfo.data.survey);
      setMtInfo(mtInfo.data.mailTemplate);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    readDB();
  }, [usr]);

  // グループを変更した場合
  const onChangeGroup = async (value) => {
    setSelGroupCode(value);
    const selGroup = groups.find((item) => item.group_code === value);
    setSelGroupInfo(selGroup);
    setReservations([]); // グループ変更時に予約リストをクリア
  };

  // 新しい予約行を追加
  const addReservationRow = () => {
    const newReservation = {
      key: Date.now(), // ユニークなキー
      survey_season_id: '',
      question_set_id: '',
      isAnswerd: '9', // デフォルトは全体
      send_date_time: null,
      survey_code: '',
      mt_id: '',
      cc: '',
      bcc: DEFAULT_BCC_EMAIL,
    };
    setReservations([...reservations, newReservation]);
  };

  // 予約行を削除
  const deleteReservationRow = (key) => {
    setReservations(reservations.filter((item) => item.key !== key));
  };

  // 予約行を複製
  const copyReservationRow = (key) => {
    const targetIndex = reservations.findIndex((item) => item.key === key);
    if (targetIndex === -1) return;

    const targetRow = reservations[targetIndex];
    const copiedRow = {
      ...targetRow,
      key: Date.now(),
    };

    const newReservations = [
      ...reservations.slice(0, targetIndex + 1),
      copiedRow,
      ...reservations.slice(targetIndex + 1),
    ];
    setReservations(newReservations);
  };

  // 予約データを更新
  const updateReservation = (key, field, value) => {
    setReservations(
      reservations.map((item) => {
        if (item.key === key) {
          const updated = { ...item, [field]: value };

          // サーベイシーズン変更時に関連情報をリセット
          // シーズンに紐付く survey_code を自動設定し、
          // 「サーベイシーズン」と「サーベイ種類」の不一致を防ぐ
          if (field === 'survey_season_id') {
            updated.question_set_id = '';
            const seasonInfo =
              selGroupInfo &&
              selGroupInfo.survey_season &&
              selGroupInfo.survey_season.find((s) => s._id === value);
            if (seasonInfo && seasonInfo.survey_code) {
              updated.survey_code = seasonInfo.survey_code;
              // survey_code が変わるとテンプレートのフィルタも変わるためリセット
              updated.mt_id = '';
            }
          }

          // サーベイコード変更時にテンプレートをリセット
          if (field === 'survey_code') {
            updated.mt_id = '';
          }

          return updated;
        }
        return item;
      })
    );
  };

  // 予約を一括登録
  const sendBulkReservate = async () => {
    try {
      // バリデーション
      if (!selGroupCode) {
        message.error('企業を選択してください。');
        return;
      }

      if (reservations.length === 0) {
        message.error('少なくとも1件の予約を追加してください。');
        return;
      }

      // 各予約の必須項目チェック
      for (let i = 0; i < reservations.length; i++) {
        const res = reservations[i];
        if (!res.survey_season_id) {
          message.error(`${i + 1}行目：サーベイシーズンを選択してください。`);
          return;
        }
        if (!res.isAnswerd) {
          message.error(`${i + 1}行目：送付先を選択してください。`);
          return;
        }
        if (!res.send_date_time) {
          message.error(`${i + 1}行目：送信予定日時を選択してください。`);
          return;
        }
        if (!res.survey_code) {
          message.error(`${i + 1}行目：サーベイ種類を選択してください。`);
          return;
        }
        if (!res.mt_id) {
          message.error(`${i + 1}行目：テンプレートを選択してください。`);
          return;
        }
      }

      message.loading({
        content: 'メール配信予約中...',
        key: 'bulk-reservate',
      });

      // 予約データを整形
      const formattedReservations = reservations.map((res) => {
        const seasonInfo = selGroupInfo.survey_season.find(
          (s) => s._id === res.survey_season_id
        );
        // シーズンに紐付く survey_code を最優先とし、survey_code / survey_name の整合性を保証
        const finalSurveyCode =
          (seasonInfo ? seasonInfo.survey_code : '') || res.survey_code;
        const surveyInfo = survey.find(
          (s) => s.survey_code === finalSurveyCode
        );
        const templateInfo = mtInfo.find((t) => t._id === res.mt_id);

        // 360度「全体」（sentinel '__ALL__'）= 本人/協力者を区別せず重複なしで1通。
        // question_set_id は空にしてシーズン単位（getMailTargets が User 単位で dedup）、
        // question_set_sub_type='ALL'（設問数は協力者基準、{$target$} は未使用）。
        // set_name は選択肢と同じ '全回答者'（予約一覧の「設問セット名」もこの値を表示）。
        const isAll = res.question_set_id === '__ALL__';

        let questionSetInfo = null;
        let questionSet = [];
        if (seasonInfo && seasonInfo.question_set) {
          questionSet = seasonInfo.question_set;
          if (res.question_set_id && !isAll) {
            questionSetInfo = questionSet.find((q) => q._id === res.question_set_id);
          }
        }

        // CC/BCCをセミコロン区切りから配列に変換
        const ccString = res.cc
          .replace(/\s+/g, '')
          .replace(/(^[;]+)|([;]+$)/g, '');
        const bccString = res.bcc
          .replace(/\s+/g, '')
          .replace(/(^[;]+)|([;]+$)/g, '');

        let ccArr = [];
        let bccArr = [];

        if (ccString) {
          ccString.split(';').forEach((email) => {
            if (email) ccArr.push({ email });
          });
        }

        if (bccString) {
          bccString.split(';').forEach((email) => {
            if (email) bccArr.push({ email });
          });
        }

        return {
          group_code: selGroupCode,
          group_name: selGroupInfo.group_name,
          survey_name: surveyInfo ? surveyInfo.survey_name : '',
          // シーズン自体に保持されている survey_code を最優先で使用（UI上の
          // 「サーベイ種類」ドロップダウンとの不一致による誤保存を防止）
          survey_code: finalSurveyCode,
          survey_id: seasonInfo ? seasonInfo.survey_id : '',
          season_name: seasonInfo ? seasonInfo.season_name : '',
          survey_season_id: res.survey_season_id,
          set_name: isAll
            ? '全回答者'
            : questionSetInfo
            ? questionSetInfo.set_name
            : '',
          question_set_id: isAll ? '' : res.question_set_id || '',
          question_set_sub_type: isAll
            ? 'ALL'
            : questionSetInfo
            ? questionSetInfo.sub_type
            : '',
          mt_id: res.mt_id,
          mt_name: templateInfo ? templateInfo.mt_name : '',
          cc: ccArr,
          bcc: bccArr,
          send_date_time: res.send_date_time.toDate(),
          isAnswerd: res.isAnswerd,
        };
      });

      const result = await Axios.post('/api/sendMail/reservate/bulk', {
        reservations: formattedReservations,
      });

      if (result.data.success) {
        message.success({
          content: `${result.data.savedCount}件の予約が完了しました。${
            result.data.errorCount > 0
              ? `（${result.data.errorCount}件失敗）`
              : ''
          }`,
          key: 'bulk-reservate',
          duration: 3,
        });

        // 成功後、ホームに戻る
        setTimeout(() => {
          props.history.push('/');
        }, 1500);
      } else {
        message.error({
          content: '予約に失敗しました。',
          key: 'bulk-reservate',
          duration: 2,
        });
      }
    } catch (error) {
      console.log(error);
      message.error({
        content: 'メール配信予約で問題が発生しました。',
        key: 'bulk-reservate',
        duration: 2,
      });
    }
  };

  const showConfirm = async () => {
    confirm({
      title: `${reservations.length}件のメールを本当に予約配信しますか？`,
      content:
        'メール配信予約処理が完了し、ホームへ自動遷移されるまで、そのままお待ちください。予約時間になると、その時点での対象者に自動的にメールが送信されます。',
      async onOk() {
        await sendBulkReservate();
      },
      async onCancel() {
        message.info({
          content: 'メール配信予約を取り消しました。',
          key: 'bulk-reservate',
          duration: 2,
        });
      },
    });
  };

  // テーブルのカラム定義
  const columns = [
    {
      title: 'No',
      key: 'index',
      width: 50,
      render: (text, record, index) => index + 1,
    },
    {
      title: 'サーベイシーズン',
      key: 'survey_season_id',
      width: 200,
      render: (text, record) => (
        <Select
          showSearch
          style={{ width: '100%' }}
          placeholder="シーズン選択"
          optionFilterProp="children"
          value={record.survey_season_id || undefined}
          onChange={(value) =>
            updateReservation(record.key, 'survey_season_id', value)
          }
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {selGroupInfo &&
            selGroupInfo.survey_season &&
            selGroupInfo.survey_season.map((element) => (
              <Option value={element._id} key={element._id}>
                {element.season_name}
              </Option>
            ))}
        </Select>
      ),
    },
    {
      title: '設問セット',
      key: 'question_set_id',
      width: 150,
      render: (text, record) => {
        const seasonInfo =
          selGroupInfo &&
          selGroupInfo.survey_season &&
          selGroupInfo.survey_season.find(
            (s) => s._id === record.survey_season_id
          );
        const questionSets =
          seasonInfo && seasonInfo.question_set ? seasonInfo.question_set : [];

        if (questionSets.length === 0) {
          return <span style={{ color: 'var(--grey-65)' }}>なし</span>;
        }

        // 360度系（40010/60020）のシーズンでは「全体」を選べる。
        // 「全体」= 本人/協力者を区別せず重複なしで1通配信（sentinel '__ALL__'）。
        const is360 = ['40010', '60020'].includes(seasonInfo.survey_code);

        return (
          <Select
            showSearch
            style={{ width: '100%' }}
            placeholder="設問セット選択"
            optionFilterProp="children"
            value={record.question_set_id || undefined}
            onChange={(value) =>
              updateReservation(record.key, 'question_set_id', value)
            }
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {is360 && (
              <Option value="__ALL__" key="__ALL__">
                全回答者
              </Option>
            )}
            {questionSets.map((element) => (
              <Option value={element._id} key={element._id}>
                {element.set_name}
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: '送付先',
      key: 'isAnswerd',
      width: 140,
      render: (text, record) => (
        <Select
          style={{ width: '100%' }}
          value={record.isAnswerd}
          onChange={(value) => updateReservation(record.key, 'isAnswerd', value)}
        >
          <Option value="9">全体</Option>
          <Option value="0">リマインド</Option>
          <Option value="SEASON_ADMIN">シーズン管理者</Option>
          <Option value="COMPANY_ADMIN">企業管理者</Option>
        </Select>
      ),
    },
    {
      title: '送信予定日時',
      key: 'send_date_time',
      width: 180,
      render: (text, record) => (
        <DatePicker
          showTime
          format="YYYY-MM-DD HH:mm"
          placeholder="日時選択"
          value={record.send_date_time}
          onChange={(date) =>
            updateReservation(record.key, 'send_date_time', date)
          }
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'サーベイ種類',
      key: 'survey_code',
      width: 180,
      render: (text, record) => (
        <Select
          showSearch
          style={{ width: '100%' }}
          placeholder="サーベイ選択"
          optionFilterProp="children"
          value={record.survey_code || undefined}
          onChange={(value) =>
            updateReservation(record.key, 'survey_code', value)
          }
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {survey.map((element) => (
            <Option value={element.survey_code} key={element._id}>
              {element.survey_name}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'テンプレート',
      key: 'mt_id',
      width: 250,
      render: (text, record) => (
        <Select
          showSearch
          style={{ width: '100%' }}
          placeholder="テンプレート選択"
          optionFilterProp="children"
          value={record.mt_id || undefined}
          onChange={(value) => updateReservation(record.key, 'mt_id', value)}
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {mtInfo
            .filter((element) => element.survey_code === record.survey_code)
            .map((element) => (
              <Option value={element._id} key={element._id}>
                {element.mt_name}
              </Option>
            ))}
        </Select>
      ),
    },
    {
      title: 'CC',
      key: 'cc',
      width: 150,
      render: (text, record) => (
        <Input
          placeholder="example@mail.com"
          value={record.cc}
          onChange={(e) => updateReservation(record.key, 'cc', e.target.value)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'BCC',
      key: 'bcc',
      width: 150,
      render: (text, record) => (
        <Input
          placeholder="example@mail.com"
          value={record.bcc}
          onChange={(e) => updateReservation(record.key, 'bcc', e.target.value)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (text, record) => (
        <Fragment>
          <Button
            type="default"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => copyReservationRow(record.key)}
            style={{ marginRight: 4 }}
          />
          <Popconfirm
            title="この行を削除しますか？"
            onConfirm={() => deleteReservationRow(record.key)}
            okText="はい"
            cancelText="いいえ"
          >
            <Button type="danger" size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Fragment>
      ),
    },
  ];

  return (
    <PageContainer fluid>
      <h2>複数メール送信予約（全体/リマインド）</h2>

        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <Text strong>■企業選択</Text>
          <br />
          <Select
            showSearch
            style={{
              marginTop: 10,
              marginBottom: 10,
              width: 300,
            }}
            placeholder="企業選択"
            optionFilterProp="children"
            onChange={onChangeGroup}
            value={selGroupCode || undefined}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {groups.map((element) => (
              <Option value={element.group_code} key={element._id}>
                {element.group_name}
              </Option>
            ))}
          </Select>
        </div>

        {selGroupCode && (
          <Fragment>
            <div style={{ marginBottom: 16 }}>
              <Button
                icon={<PlusOutlined />}
                onClick={addReservationRow}
              >
                予約行を追加
              </Button>
              <span style={{ marginLeft: 16, color: 'var(--grey-65)' }}>
                現在の予約件数: {reservations.length}件
              </span>
            </div>

            <Table
              dataSource={reservations}
              columns={columns}
              pagination={false}
              scroll={{ x: 1600 }}
              size="small"
              bordered
              locale={{
                emptyText: '「予約行を追加」ボタンをクリックして予約を追加してください',
              }}
            />

            <div style={{ marginTop: 20, marginBottom: 20 }}>
              <Text type="secondary">
                ※ CC/BCCは複数のメールアドレスをセミコロン「;」で区切って入力してください
              </Text>
              <br />
              <Text type="secondary">
                ※ 予約時間になると、その時点での対象者（全体またはリマインド）に自動的にメールが送信されます
              </Text>
              <br />
              <Text type="secondary">
                ※ テンプレート内容は発送時点の最新内容が使用されます
              </Text>
            </div>

            <Button
              type="primary"
              size="large"
              onClick={showConfirm}
              disabled={reservations.length === 0}
              style={{ marginTop: 10 }}
            >
              {reservations.length}件をまとめて予約登録
            </Button>
          </Fragment>
        )}
    </PageContainer>
  );
};

export default MultiMailSendReservatePage;
